import React, { useState, useEffect } from "react";
import MCategoryActionBar from "./MCActionBar/ActionBar";
import DynamicTable from "../../../components/Table/MTable";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import axios from "axios";
import { useSelector } from "react-redux";
import { Upload } from "lucide-react";

const MCategory = () => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const apiEndpoint = `${API_BASE_URL}/mcategory/mcategories`;
  const { role } = useSelector((state) => state.auth);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 10;

  const columns = [
    { key: "CategoryID", label: "Category ID" },
    { key: "CategoryName", label: "Category Name" },
    { key: "NormalDays", label: "Normal Days" },
    { key: "EmergencyDays", label: "Emergency Days" },
  ];

  const fetchCategories = async (page = 1, query = "") => {
    try {
      const response = await axios.get(
        `${apiEndpoint}?page=${page}&limit=${itemsPerPage}&search=${query}`
      );
      setCategories(response.data.items);
      setFilteredCategories(response.data.items);
      setTotalItems(response.data.total);
      setTotalPages(Math.ceil(response.data.total / itemsPerPage));
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleFileImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    setImporting(true);
    setImportError("");

    try {
      await axios.post(`${apiEndpoint}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      await fetchCategories(1, searchQuery);
      alert("File imported successfully!");
    } catch (error) {
      setImportError(error.response?.data?.error || "Error importing file");
    } finally {
      setImporting(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchCategories(page, searchQuery);
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching
    await fetchCategories(1, query);
  };

  useEffect(() => {
    fetchCategories(1, searchQuery);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header exportEndpoint={apiEndpoint} />

      <div className="flex-grow p-4">
        <div className="flex justify-between items-center mb-4">
          <MCategoryActionBar
            title="Categories"
            rowKey="CategoryID"
            deleteMessage="Are you sure you want to remove this category permanently?"
            fields={[
              { name: "CategoryName", label: "Category Name", required: true },
              { name: "NormalDays", label: "Normal Days", type: "number" },
              { name: "EmergencyDays", label: "Emergency Days", type: "number" },
            ]}
            apiEndpoint={apiEndpoint}
            data={filteredCategories}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            onRefresh={() => fetchCategories(currentPage, searchQuery)}
            onSearch={handleSearch}
            userRole={role}
          />
          
          <div className="flex items-center">
            <label className="relative cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center">
              <Upload className="mr-2" size={20} />
              {importing ? "Importing..." : "Import CSV/ODS"}
              <input
                type="file"
                className="hidden"
                accept=".csv,.ods"
                onChange={handleFileImport}
                disabled={importing}
              />
            </label>
            {importError && (
              <div className="text-red-500 ml-2">{importError}</div>
            )}
          </div>
        </div>

        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">Category Table</h3>
        </div>

        <div className="mt-4">
          <DynamicTable
            columns={columns}
            data={filteredCategories}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            rowKey="CategoryID"
            highlightColor="bg-green-100"
            noDataMessage="No categories found."
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
          />
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MCategory;